/**
 * GMPCatalog — Phase 2A B2B
 *
 * Composants :
 *   - GMPCatalogButton  : bouton "Catalogue B2B" + modale d'import + stats
 *   - GMPProductAutocomplete : champ texte avec suggestions catalogue
 *
 * Naming note: la fonctionnalité s'appelait initialement « Catalogue GMP »
 * (du nom du 1er client Groupe Médical Prométhée). Renommée en « Catalogue B2B »
 * pour pouvoir importer des catalogues d'autres cliniques sans confusion.
 * Les noms de fichiers/endpoints/collection MongoDB restent en `gmp_catalog`
 * pour ne pas casser les données existantes — c'est invisible à l'utilisateur.
 *
 * Endpoints backend utilisés :
 *   - POST   /api/b2b/gmp/catalog/import   (multipart, .xlsx)
 *   - GET    /api/b2b/gmp/catalog/stats
 *   - GET    /api/b2b/gmp/catalog/search?q=
 *   - DELETE /api/b2b/gmp/catalog          (admin/supervisor)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Database, X, Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

// ─── Bouton + modale d'import ─────────────────────────────────────────────

export const GMPCatalogButton = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/b2b/gmp/catalog/stats`, { headers: auth() });
      setStats(r.data);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    if (open) { loadStats(); }
  }, [open, loadStats]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('Format non supporté — fournissez un fichier .xlsx');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/b2b/gmp/catalog/import`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Import OK : ${r.data.nb_inserted} ajoutés · ${r.data.nb_updated} mis à jour`);
      await loadStats();
      onChange?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : "Erreur lors de l'import");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onClear = async () => {
    if (!window.confirm('Vider entièrement le catalogue B2B ? Cette action est irréversible.')) return;
    try {
      const r = await axios.delete(`${API}/b2b/gmp/catalog`, { headers: auth() });
      toast.success(`Catalogue vidé · ${r.data.deleted} produits supprimés`);
      setStats({ total: 0, last_import_at: null, last_import_by: null });
      setResult(null);
      onChange?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors du vidage');
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-violet-300 text-violet-700 hover:bg-violet-50"
        data-testid="gmp-catalog-btn"
      >
        <Database className="w-4 h-4 mr-1" /> Catalogue B2B
        {stats?.total > 0 && (
          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold">
            {stats.total}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3"
          onClick={() => setOpen(false)}
          data-testid="gmp-catalog-modal"
        >
          <div
            className="bg-white rounded-xl max-w-lg w-full max-h-[calc(100dvh-80px)] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-5 py-3 text-white flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2">
                <Database className="w-5 h-5" /> Catalogue B2B
              </h2>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" data-testid="gmp-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm">
                <p className="text-violet-900 font-medium">État actuel</p>
                {stats ? (
                  <>
                    <p className="text-zinc-700 mt-1">
                      <span className="text-2xl font-bold text-violet-700">{stats.total}</span>
                      <span className="ml-1 text-xs">produit(s) en catalogue</span>
                    </p>
                    {stats.last_import_at && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Dernier import :{' '}
                        {new Date(stats.last_import_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                        {stats.last_import_by ? ` par ${stats.last_import_by}` : ''}
                      </p>
                    )}
                  </>
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-zinc-800 mb-2">Importer un nouveau listing</p>
                <p className="text-xs text-zinc-500 mb-3">
                  Fichier Excel Médiciel (.xlsx). La ligne d'en-tête est détectée automatiquement
                  (cherche « Code Produit » + « Produit »). Les produits existants seront mis à jour
                  (clé : code), les nouveaux ajoutés.
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onFile}
                  disabled={uploading}
                  className="block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100 disabled:opacity-50"
                  data-testid="gmp-import-file"
                />
                {uploading && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-violet-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Import en cours…
                  </div>
                )}
              </div>

              {result && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <p className="font-semibold text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Import terminé · ligne d'en-tête détectée en R{result.header_row}
                  </p>
                  <ul className="mt-2 text-emerald-800 text-xs space-y-0.5">
                    <li>✅ {result.nb_inserted} nouveau(x) produit(s)</li>
                    <li>♻️ {result.nb_updated} produit(s) mis à jour</li>
                    <li>⏭️ {result.nb_skipped} ligne(s) ignorée(s) (vides / totaux)</li>
                    {result.errors?.length > 0 && (
                      <li className="text-amber-700 mt-1">⚠️ {result.errors.length} avertissement(s)</li>
                    )}
                  </ul>
                </div>
              )}

              {stats?.total > 0 && (
                <div className="pt-2 border-t border-zinc-200">
                  <button
                    type="button"
                    onClick={onClear}
                    className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                    data-testid="gmp-clear-btn"
                  >
                    <Trash2 className="w-3 h-3" /> Vider le catalogue (admin/superviseur)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Autocomplete pour les items dossier B2B ─────────────────────────────

export const GMPProductAutocomplete = ({ value, onChange, onSelect, placeholder, ...rest }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const timer = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/b2b/gmp/catalog/search`, {
        params: { q, limit: 8 },
        headers: auth(),
      });
      setSuggestions(r.data || []);
      setOpen(r.data?.length > 0);
      setHighlightIdx(-1);
    } catch (_) {
      setSuggestions([]);
      setOpen(false);
    }
  }, []);

  const onInput = (e) => {
    const v = e.target.value;
    onChange?.(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 180);
  };

  const pickSuggestion = (s) => {
    onSelect?.(s);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={value || ''}
        onChange={onInput}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Désignation produit'}
        autoComplete="off"
        {...rest}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg text-sm">
          {suggestions.map((s, i) => (
            <li
              key={s.id || s.code}
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              onMouseEnter={() => setHighlightIdx(i)}
              className={`px-3 py-2 cursor-pointer ${i === highlightIdx ? 'bg-violet-50' : 'hover:bg-zinc-50'}`}
              data-testid={`gmp-suggestion-${i}`}
            >
              <p className="font-medium text-zinc-900 truncate">{s.name}</p>
              <p className="text-xs text-zinc-500 font-mono flex justify-between">
                <span>code {s.code}</span>
                {s.price_vente_ttc != null && (
                  <span className="text-violet-700 font-semibold">
                    {Math.round(s.price_vente_ttc).toLocaleString('fr-FR')} FCFA
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GMPCatalogButton;
